/**
 * CareMatters II / CareMatters Together calculation primitives.
 *
 * CareMatters has unique mechanics that don't fit the COB-pool
 * model used by other hybrid life products:
 *
 *   - Flat **base pool** of `baseMonthly × benefitPeriodMonths` that does
 *     not inflate. Depletion of this pool — in base dollars only — is what
 *     stops LTC payments.
 *   - **Inflation rider** is a per-policy-year monthly stream paid on top
 *     of the flat base monthly. It grows by policy year regardless of
 *     claim status. Year 1 = $0; year 2 = baseMonthly × rate; etc.
 *   - **Rider forfeiture**: any month where less than the full base
 *     monthly is taken → rider = $0 for that month. The plan assumes
 *     full-benefit utilization — the forfeiture rule is surfaced as UI
 *     copy, not modeled here.
 *   - **Death benefit** is reduced by base dollars paid only (rider
 *     payments do not reduce DB), floored at the policy's minimum
 *     guaranteed amount when set.
 *   - **Rider duration** varies by form: lifetime (e.g. ICC18-NWLA-586) or
 *     capped at a fixed number of years (e.g. ICC21-NWLA-615 caps at 20
 *     years: 20 annual increases, freezing at the year-21 value).
 */

export type CareMattersRiderForm = {
  /** Inflation rate as a decimal (e.g. 0.03 for 3%). */
  rate: number;
  /** True = simple inflation, false = compound. */
  simple: boolean;
  /**
   * Number of annual increases the rider applies before freezing. A value of
   * `N` means N increases (the rider keeps stepping up through policy year
   * `N + 1`, then holds). `null` (or 0) = lifetime — rider keeps growing for
   * the life of the policy.
   */
  capYears: number | null;
};

/**
 * Which of the two CareMatters inflation architectures a policy uses.
 *
 * `riderBeside` — CareMatters II outside New York (inflation rider form
 * ICC18-NWLA-586 and friends). Inflation is a standalone rider that pays its
 * own money beside the base pool: rider dollars neither deplete the LTC pool
 * nor reduce the death benefit, so a full claim pays out MORE than the
 * specified amount. Confirmed against the Alabama illustration, which carries
 * "Inflation Rider Benefit" as its own ledger column starting at $0 in year 1
 * while the death benefit column stays flat.
 *
 * `cappedAcceleration` — CareMatters II in New York where no Extension of
 * Benefits rider is in force (benefit duration 2 years, LTCEB premium $0).
 * NY folds inflation into the acceleration rider (ADBQSIP, form NWLA-653-NY)
 * instead of selling it beside: there is no separate inflation money, so a
 * larger monthly benefit simply exhausts the specified amount sooner. Total
 * payout is capped at the specified amount and every dollar paid — base and
 * inflation alike — draws down the death benefit.
 *
 * NY policies that DO carry the Extension of Benefits rider (duration 3+) use
 * `riderBeside`: the carrier pays the full inflated monthly "until the Total
 * LTC Benefit (excluding Inflation) is exhausted", which is the same flat-base-
 * pool clock this module already implements. Verified against the NY 7-year
 * illustration: ADB $74,400 (24 months) + EOB $186,000 (60 months) = $260,400
 * = 84 × $3,100 base.
 */
export type CareMattersBenefitMode = 'riderBeside' | 'cappedAcceleration';

/**
 * Rider's extra monthly amount at the start of the given policy year.
 *
 * The rider steps up *discretely* on each policy anniversary — within a
 * given policy year the value is flat. Fractional `policyYear` inputs are
 * floored before lookup so callers cannot accidentally produce continuous
 * compounding.
 *
 * Year 1 = $0 (rider does not pay in policy year 1; it pays the *increase*
 * over the base monthly, and there is no increase yet). Year 2 = first
 * increment (= baseMonthly × rate for compound, baseMonthly × rate for
 * simple). Compound vs. simple controlled by `form.simple`. A `capYears`-year
 * rider applies `capYears` annual increases: it keeps stepping up through
 * policy year `capYears + 1`, then freezes at that value for every later year
 * (matching carrier illustrations).
 */
export function calcRiderMonthly(
  baseMonthly: number,
  policyYear: number,
  form: CareMattersRiderForm,
): number {
  // Floor to the integer policy year — the rider only steps up on policy
  // anniversaries; fractional inputs would otherwise produce continuous
  // compounding via Math.pow with a fractional exponent.
  const intYear = Math.floor(policyYear);
  if (intYear <= 1) return 0;
  if (baseMonthly <= 0) return 0;
  if (form.rate <= 0) return 0;

  const cap = form.capYears && form.capYears > 0 ? form.capYears : null;
  // Increment count = years past the at-issue year. A `capYears`-year rider
  // applies `capYears` annual increases (the rider keeps stepping up through
  // policy year `capYears + 1`, then freezes), matching carrier illustrations.
  const rawIncrements = intYear - 1;
  const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;

  if (form.simple) {
    return baseMonthly * form.rate * increments;
  }
  // Compound: monthly_n = baseMonthly × ((1+rate)^(n-1) − 1)
  return baseMonthly * (Math.pow(1 + form.rate, increments) - 1);
}

/**
 * Total LTC Benefit projection — the "Total LTC Benefit (with inflation)"
 * number advisors quote on the policy summary. Assumes a consecutive
 * full-benefit claim (no forfeiture, no broken months).
 *
 *   total = 12 × Σ_{k=0..B−1} (baseMonthly + rider(claimStartPolicyYear + k))
 *
 * where B = floor(benefitPeriodMonths / 12).
 *
 * `claimStartPolicyYear` is the policy year in which the claim begins —
 * pre-claim: predicted LTC onset year minus policy premium start year;
 * on-claim: actual policy activation year minus policy premium start year.
 */
export function calcTotalLtcBenefitOverPeriod(input: {
  baseMonthly: number;
  benefitPeriodMonths: number;
  claimStartPolicyYear: number;
  riderForm: CareMattersRiderForm;
  mode?: CareMattersBenefitMode;
  /**
   * Required in `cappedAcceleration` mode — the ceiling on total payout.
   * Ignored in `riderBeside` mode, where the rider pays beside the pool and
   * the total legitimately exceeds the specified amount.
   */
  specifiedAmount?: number | null;
}): number {
  const { baseMonthly, benefitPeriodMonths, claimStartPolicyYear, riderForm } = input;
  if (baseMonthly <= 0 || benefitPeriodMonths <= 0) return 0;

  const benefitYears = Math.floor(benefitPeriodMonths / 12);
  let total = 0;
  for (let k = 0; k < benefitYears; k++) {
    const policyYear = Math.max(1, claimStartPolicyYear + k);
    const monthly = baseMonthly + calcRiderMonthly(baseMonthly, policyYear, riderForm);
    total += 12 * monthly;
  }

  // Capped acceleration: the acceleration rider cannot pay out more than the
  // death benefit it accelerates, so inflation buys a bigger monthly cheque
  // rather than a bigger total. Matches the NY illustration, which prints
  // "Total LTC benefit plus inflation when insured is age 85" equal to the
  // specified amount on every 2-year row regardless of inflation option.
  if (input.mode === 'cappedAcceleration' && input.specifiedAmount != null) {
    return Math.min(total, input.specifiedAmount);
  }
  return total;
}

/**
 * Per-phase simulation. Inputs come from the existing eligibility pipeline
 * (the caller reuses `handlePhaseBenefitEligibleCareDays` for 2-ADL /
 * waiting-period handling). Returns dollar amounts and updated CM II state.
 *
 * Assumes full-benefit utilization: every eligible month consumes the full
 * base monthly + the rider monthly for that policy year. Rider forfeiture
 * (partial-month claims) is not simulated — surfaced as UI copy.
 *
 * Base-pool depletion clamps month coverage: once the base pool runs out,
 * LTC payments stop, even if eligible months remain.
 */
export function simulateCareMattersPhase(input: {
  /** Full-month base benefit (= max daily × 30). */
  baseMonthly: number;
  /** Base pool dollars remaining at start of phase. */
  basePoolRemaining: number;
  riderForm: CareMattersRiderForm;
  /** Policy year at the START of this phase. */
  phasePolicyStartYear: number;
  /** Eligible months for this phase (= floor(eligibleDays / 30)). */
  phaseEligibleMonths: number;
  mode?: CareMattersBenefitMode;
}): {
  monthsCovered: number;
  baseDollarsPaidThisPhase: number;
  riderDollarsPaidThisPhase: number;
  basePoolRemainingAfterPhase: number;
  /** = base + rider total dollars out for this phase. */
  indemnityPayoutForPhase: number;
  /**
   * Dollars this phase took OUT OF THE POOL — which is also what draws down
   * the death benefit. Equals `baseDollarsPaidThisPhase` in `riderBeside`
   * mode (the rider pays beside the pool) and the full base + rider payout in
   * `cappedAcceleration` mode (there is no money beside the pool to pay from).
   */
  poolDollarsConsumedThisPhase: number;
  /** Average rider dollar amount across the months covered (0 if none). */
  averageRiderMonthlyForPhase: number;
} {
  const { baseMonthly, basePoolRemaining, riderForm, phasePolicyStartYear, phaseEligibleMonths } =
    input;
  const mode: CareMattersBenefitMode = input.mode ?? 'riderBeside';

  if (baseMonthly <= 0 || phaseEligibleMonths <= 0 || basePoolRemaining <= 0) {
    return {
      monthsCovered: 0,
      baseDollarsPaidThisPhase: 0,
      riderDollarsPaidThisPhase: 0,
      basePoolRemainingAfterPhase: Math.max(basePoolRemaining, 0),
      indemnityPayoutForPhase: 0,
      poolDollarsConsumedThisPhase: 0,
      averageRiderMonthlyForPhase: 0,
    };
  }

  // The rider steps up only on policy anniversaries, so track where this phase
  // sits within its starting policy year (months elapsed since the most recent
  // anniversary) and increment the integer policy year whenever the running
  // month-count crosses an anniversary boundary. This matters when a phase
  // straddles an anniversary mid-claim — e.g. a phase that begins 9 months into
  // year 1 and lasts 6 months should bill 3 months at year-1 rider and 3 months
  // at year-2 rider.
  const phaseStartYearFloor = Math.floor(phasePolicyStartYear);
  // Floor with a small epsilon. Floor avoids over-rounding at near-anniversary
  // values like 1.99 (Math.round would jump to 12, advancing to year 2 a
  // month early). The epsilon absorbs floating-point fuzz produced by chained
  // monthsNeeded/12 arithmetic — e.g. `1 + 11/12` evaluates to 1.9166...7 in
  // JS, multiplying out to 10.99...8; bare floor would land at 10 instead of
  // the intended 11.
  const monthsIntoStartYear = Math.floor((phasePolicyStartYear - phaseStartYearFloor) * 12 + 1e-6);

  function riderForPhaseMonth(monthOffset: number): number {
    const monthsFromYearStart = monthsIntoStartYear + monthOffset;
    const policyYear = phaseStartYearFloor + Math.floor(monthsFromYearStart / 12);
    return calcRiderMonthly(baseMonthly, policyYear, riderForm);
  }

  let monthsCovered = 0;
  let riderDollarsPaidThisPhase = 0;
  let poolDollarsConsumedThisPhase = 0;

  if (mode === 'cappedAcceleration') {
    // Base and rider come out of the SAME pool, so a month is payable only if
    // the pool can fund the whole inflated cheque. Partial months do not pay,
    // matching the floor() the riderBeside branch applies to its month count.
    for (let m = 0; m < phaseEligibleMonths; m++) {
      const rider = riderForPhaseMonth(m);
      const monthCost = baseMonthly + rider;
      if (monthCost > basePoolRemaining - poolDollarsConsumedThisPhase + 1e-6) break;
      poolDollarsConsumedThisPhase += monthCost;
      riderDollarsPaidThisPhase += rider;
      monthsCovered++;
    }
  } else {
    // The base pool caps months covered: each month consumes baseMonthly from
    // the pool. Take whichever is smaller — eligible months or pool-allowed
    // months. Use floor for pool-allowed; partial months don't pay.
    // Epsilon absorbs floating-point fuzz so an exact multiple like
    // 144918.72 / 3019.14 (mathematically 48) doesn't land at 47.999…9 and
    // floor down to 47, leaving a phantom base-monthly stranded in the pool.
    const poolAllowedMonths = Math.floor(basePoolRemaining / baseMonthly + 1e-6);
    monthsCovered = Math.min(phaseEligibleMonths, poolAllowedMonths);
    for (let m = 0; m < monthsCovered; m++) {
      riderDollarsPaidThisPhase += riderForPhaseMonth(m);
    }
    poolDollarsConsumedThisPhase = monthsCovered * baseMonthly;
  }

  if (monthsCovered <= 0) {
    return {
      monthsCovered: 0,
      baseDollarsPaidThisPhase: 0,
      riderDollarsPaidThisPhase: 0,
      basePoolRemainingAfterPhase: basePoolRemaining,
      indemnityPayoutForPhase: 0,
      poolDollarsConsumedThisPhase: 0,
      averageRiderMonthlyForPhase: 0,
    };
  }

  const baseDollarsPaidThisPhase = monthsCovered * baseMonthly;
  const basePoolRemainingAfterPhase = Math.max(
    basePoolRemaining - poolDollarsConsumedThisPhase,
    0,
  );
  const indemnityPayoutForPhase = baseDollarsPaidThisPhase + riderDollarsPaidThisPhase;
  const averageRiderMonthlyForPhase =
    monthsCovered > 0 ? riderDollarsPaidThisPhase / monthsCovered : 0;

  return {
    monthsCovered,
    baseDollarsPaidThisPhase,
    riderDollarsPaidThisPhase,
    basePoolRemainingAfterPhase,
    indemnityPayoutForPhase,
    poolDollarsConsumedThisPhase,
    averageRiderMonthlyForPhase,
  };
}

/**
 * Death benefit at claim end horizon. What reduces the DB is pool consumption,
 * so the dollars to pass in depend on the policy's benefit mode:
 *
 *   - `riderBeside` (CareMatters II outside NY): rider payments do NOT reduce
 *     the DB, so pass base-pool consumption only. The Alabama illustration
 *     shows the death benefit flat at the specified amount while the inflation
 *     rider column grows.
 *   - `cappedAcceleration` (NY without an Extension of Benefits rider): the
 *     inflation IS the acceleration, so every dollar paid reduces the DB —
 *     pass `poolDollarsConsumedThisPhase` from `simulateCareMattersPhase`.
 *
 * The parameter keeps the name `baseDollarsPaid` because it feeds a
 * long-standing accumulator of the same name across the legacy calc path;
 * read it as "dollars that drew down the pool".
 *
 * When the policy specifies a minimum guaranteed amount, the DB is floored
 * there; otherwise no synthetic floor is applied.
 */
export function calcCareMattersDeathBenefit(input: {
  /** = policyMaximumBenefitAmount (the face / specified amount). */
  specifiedAmount: number;
  /** Accumulator carry of base-pool dollars consumed during the claim. */
  baseDollarsPaid: number;
  /** policyMinimumGuaranteedBenefitAmount, if advisor-set. */
  storedFloorAmount?: number | null;
}): number {
  const { specifiedAmount, baseDollarsPaid, storedFloorAmount } = input;
  const floor = storedFloorAmount ?? 0;
  return Math.max(specifiedAmount - baseDollarsPaid, floor);
}
